/**
 * BorderCrossingService.ts
 * 
 * A utility service for detecting border crossings along a route.
 * Provides functions for:
 * - Detecting border crossings in route geometry
 * - Calculating distances to and from border crossings
 */

import { BorderCrossing } from '../types/BorderCrossingTypes';
import { withCache } from './CacheService';
import { decodePolyline } from '../utils/polyline';
import { getCountryForCoordinate } from './api/geocodingApi';

// Cache expiration time (in milliseconds)
const BORDER_CROSSING_CACHE_EXPIRATION = 14 * 24 * 60 * 60 * 1000; // 14 days

/**
 * Extracts coordinates from route geometry
 * @param geometry The route geometry (encoded polyline string or GeoJSON)
 * @returns Array of [lat, lng] coordinates
 */
const extractCoordinates = (geometry: any): [number, number][] => {
  if (typeof geometry === 'string') {
    // If geometry is encoded polyline, decode it
    return decodePolyline(geometry);
  } else if (geometry.coordinates && Array.isArray(geometry.coordinates)) {
    // If geometry is GeoJSON, convert coordinates to [lat, lng] format
    return geometry.coordinates.map((coord: [number, number]) => [coord[1], coord[0]]);
  }
  
  return [];
};

/**
 * Calculates the distance between two coordinates in kilometers
 * @param lat1 Latitude of the first coordinate
 * @param lng1 Longitude of the first coordinate
 * @param lat2 Latitude of the second coordinate
 * @param lng2 Longitude of the second coordinate
 * @returns Distance in kilometers
 */
const calculateHaversineDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return distance;
};

/**
 * Samples points along a route at regular intervals.
 * Uses 50 km interval to reduce reverse-geocode API calls (e.g. ~60 points for 3000 km instead of ~300).
 */
const sampleRoutePoints = (
  coordinates: [number, number][],
  sampleInterval: number = 50
): { coordinate: [number, number]; distance: number }[] => {
  if (coordinates.length < 2) {
    return [];
  }
  
  const sampledPoints: { coordinate: [number, number]; distance: number }[] = [
    { coordinate: coordinates[0], distance: 0 } // Start point
  ];
  
  let totalDistance = 0;
  let lastSampleDistance = 0;
  
  for (let i = 1; i < coordinates.length; i++) {
    const prevCoord = coordinates[i - 1];
    const currCoord = coordinates[i];
    
    const segmentDistance = calculateHaversineDistance(
      prevCoord[0], prevCoord[1],
      currCoord[0], currCoord[1]
    );
    
    totalDistance += segmentDistance;
    
    // If we've traveled at least sampleInterval km since the last sample, add a new sample
    if (totalDistance - lastSampleDistance >= sampleInterval) {
      sampledPoints.push({ coordinate: currCoord, distance: totalDistance });
      lastSampleDistance = totalDistance;
    }
  }
  
  // Always include the end point
  if (sampledPoints[sampledPoints.length - 1].coordinate !== coordinates[coordinates.length - 1]) {
    sampledPoints.push({ 
      coordinate: coordinates[coordinates.length - 1], 
      distance: totalDistance 
    });
  }
  
  return sampledPoints;
};

/**
 * Detects border crossings along a route
 * @param geometry The route geometry (encoded polyline string or GeoJSON)
 * @param language The language code (e.g., 'ru' or 'en')
 * @param segmentIndex The index of the route segment
 * @param segmentStartDistance The distance from the start of the route to the start of this segment
 * @returns Promise with an array of border crossings
 */
const detectBorderCrossingsImpl = async (
  geometry: any,
  language: string = 'en',
  segmentIndex: number = 0,
  segmentStartDistance: number = 0
): Promise<BorderCrossing[]> => {
  try {
    const coordinates = extractCoordinates(geometry);
    if (coordinates.length < 2) return [];

    const start = coordinates[0];
    const end = coordinates[coordinates.length - 1];

    // Быстрая проверка: только 2 запроса (начало и конец сегмента)
    const [startCountry, endCountry] = await Promise.all([
      getCountryForCoordinate(start[0], start[1], language),
      getCountryForCoordinate(end[0], end[1], language)
    ]);

    // Разные страны — граница есть, нужен полный сэмплинг
    if (startCountry && endCountry && startCountry !== endCountry) {
      return runFullBorderDetection(
        coordinates,
        language,
        segmentIndex,
        segmentStartDistance
      );
    }

    // Одна страна — проверяем середину (2 точки): вдруг маршрут заходит в другую страну и возвращается
    const mid1 = coordinates[Math.floor(coordinates.length / 3)];
    const mid2 = coordinates[Math.floor((2 * coordinates.length) / 3)];
    const [mid1Country, mid2Country] = await Promise.all([
      getCountryForCoordinate(mid1[0], mid1[1], language),
      getCountryForCoordinate(mid2[0], mid2[1], language)
    ]);

    const baseCountry = startCountry ?? endCountry;
    const midSame = (baseCountry && mid1Country === baseCountry && mid2Country === baseCountry);
    if (midSame) {
      return []; // границ нет, не делаем полный сэмплинг
    }

    // В середине другая страна — делаем полный сэмплинг
    return runFullBorderDetection(
      coordinates,
      language,
      segmentIndex,
      segmentStartDistance
    );
  } catch (err) {
    console.error('Error detecting border crossings:', err);
    return [];
  }
};

/**
 * Полное определение границ: сэмплинг по маршруту каждые 50 км и поиск смены страны.
 */
async function runFullBorderDetection(
  coordinates: [number, number][],
  language: string,
  segmentIndex: number,
  segmentStartDistance: number
): Promise<BorderCrossing[]> {
  const sampledPoints = sampleRoutePoints(coordinates);
  const BATCH_SIZE = 8;
  const pointsWithCountry: {
    coordinate: [number, number];
    distance: number;
    country: string | null;
  }[] = [];

  for (let i = 0; i < sampledPoints.length; i += BATCH_SIZE) {
    const batch = sampledPoints.slice(i, i + BATCH_SIZE);
    const countries = await Promise.all(
      batch.map((p) =>
        getCountryForCoordinate(p.coordinate[0], p.coordinate[1], language)
      )
    );
    batch.forEach((point, j) => {
      pointsWithCountry.push({
        coordinate: point.coordinate,
        distance: point.distance,
        country: countries[j] ?? null
      });
    });
  }

  const borderCrossings: BorderCrossing[] = [];
  for (let i = 1; i < pointsWithCountry.length; i++) {
    const prevPoint = pointsWithCountry[i - 1];
    const currPoint = pointsWithCountry[i];
    if (
      prevPoint.country &&
      currPoint.country &&
      prevPoint.country !== currPoint.country
    ) {
      const borderLat = (prevPoint.coordinate[0] + currPoint.coordinate[0]) / 2;
      const borderLng = (prevPoint.coordinate[1] + currPoint.coordinate[1]) / 2;
      const distanceToBorder = (currPoint.distance - prevPoint.distance) / 2;
      borderCrossings.push({
        name: `${prevPoint.country}-${currPoint.country}`,
        fromCountry: prevPoint.country,
        toCountry: currPoint.country,
        distanceToBorder: prevPoint.distance + distanceToBorder - segmentStartDistance,
        distanceFromBorder: currPoint.distance - (prevPoint.distance + distanceToBorder),
        segmentIndex,
        coordinates: { lat: borderLat, lng: borderLng }
      });
    }
  }
  return borderCrossings;
}

/**
 * Cached version of detectBorderCrossings
 * Uses the withCache higher-order function to cache responses
 */
export const detectBorderCrossings = withCache(
  detectBorderCrossingsImpl,
  'bordercrossing:detect',
  BORDER_CROSSING_CACHE_EXPIRATION
);